import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Check, Clock, ShieldCheck, Sparkles } from "lucide-react";
import { MaskText, FadeUp, SectionHeading } from "../components/Reveal";
import { INDUSTRIES, SPACE_TYPES, TIMELINES, BUDGETS, HEAR_ABOUT, STEPS, IMAGES } from "../data/site";
import { submitEnquiry } from "../lib/api";

const initial = {
  name: "", company: "", position: "", email: "", phone: "",
  industry: "", space_type: "", space_size: "", spaces: "",
  timeline: "", budget: "", hear_about: "", message: "",
};

const inputCls =
  "w-full bg-transparent border-b border-[#cbbda6] py-3 text-[#201a12] placeholder:text-[#9a8d78] focus:border-gold focus:outline-none transition-colors duration-300";
const selectCls = `${inputCls} appearance-none cursor-pointer`;

const Field = ({ label, children }) => (
  <label className="block">
    <span className="eyebrow text-[0.6rem] block mb-3">{label}</span>
    {children}
  </label>
);

const HowItWorks = () => (
  <section className="relative bg-[#241a12] overflow-hidden" data-testid="enquiry-how-it-works">
    <img src={IMAGES.charcoalMarble} alt="" className="absolute inset-0 w-full h-full object-cover opacity-18" />
    <div className="absolute inset-0 bg-[#241a12]/80" />
    <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-28">
      <SectionHeading eyebrow="How it works" title="Effortless from day one" dark className="mb-16" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-14">
        {STEPS.map((s, i) => (
          <FadeUp key={s.no} delay={i * 0.1} data-testid={`enquiry-step-${i}`}>
            <div className="flex items-baseline gap-4">
              <span className="font-serif text-5xl md:text-6xl text-gold leading-none">{s.no}</span>
              <span className="h-px flex-1 bg-[#3a352c] translate-y-[-0.4rem]" />
            </div>
            <h3 className="font-serif text-2xl text-[#f4f2ec] mt-6 mb-3">{s.title}</h3>
            <p className="text-[#9a968c] leading-relaxed">{s.body}</p>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

export default function BusinessEnquiries() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await submitEnquiry(form);
      setDone(true);
      toast.success("Enquiry received. Our commercial team will be in touch shortly.");
      setForm(initial);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="enquiries-page">
      {/* Hero */}
      <section className="relative bg-cream pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.heroBrand} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#f4eee2] via-[#f4eee2]/85 to-[#f4eee2]" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
          <FadeUp><p className="eyebrow mb-6">Business Scenting Enquiries</p></FadeUp>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight text-ink max-w-3xl">
            <MaskText lines={["Let's scent", "your business."]} delay={0.2} />
          </h1>
          <FadeUp delay={0.3}>
            <p className="text-ink-muted leading-relaxed mt-8 max-w-xl">
              Tell us a bit about your space and we'll recommend the right setup and send a
              no-obligation quote — usually within a business day.
            </p>
          </FadeUp>
          <FadeUp delay={0.4}>
            <div className="flex flex-wrap gap-x-10 gap-y-4 mt-10">
              {[
                { icon: Clock, label: "1 business-day response" },
                { icon: ShieldCheck, label: "No obligation, free consultation" },
                { icon: Sparkles, label: "30+ signature fragrances" },
              ].map((t) => (
                <div key={t.label} className="flex items-center gap-3">
                  <t.icon size={17} className="text-gold shrink-0" />
                  <span className="text-sm text-ink-muted">{t.label}</span>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Form */}
      <section className="bg-cream pb-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-8">
              {done ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-goldline p-12 text-center"
                  data-testid="enquiry-success"
                >
                  <div className="w-14 h-14 rounded-full border border-gold grid place-items-center mx-auto mb-6">
                    <Check size={24} className="text-gold" />
                  </div>
                  <h3 className="font-serif text-3xl text-ink mb-3">Thank you.</h3>
                  <p className="text-ink-muted max-w-md mx-auto">
                    Your enquiry has been received. A commercial scent consultant will reach out
                    within one business day.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    className="mt-8 text-gold text-xs font-semibold uppercase tracking-[0.2em] link-underline"
                    data-testid="enquiry-reset"
                  >
                    Submit another enquiry
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={submit} className="space-y-12" data-testid="enquiry-form">
                  <div>
                    <p className="eyebrow mb-8">1 — About you</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                      <Field label="Full Name *">
                        <input data-testid="enq-name" className={inputCls} placeholder="Jane Doe" value={form.name} onChange={set("name")} />
                      </Field>
                      <Field label="Business Email *">
                        <input data-testid="enq-email" type="email" className={inputCls} placeholder="jane@company.com" value={form.email} onChange={set("email")} />
                      </Field>
                      <Field label="Phone">
                        <input data-testid="enq-phone" className={inputCls} placeholder="0400 000 000" value={form.phone} onChange={set("phone")} />
                      </Field>
                      <Field label="Company">
                        <input data-testid="enq-company" className={inputCls} placeholder="Company name" value={form.company} onChange={set("company")} />
                      </Field>
                      <Field label="Your Role / Position">
                        <input data-testid="enq-position" className={inputCls} placeholder="e.g. Operations Manager" value={form.position} onChange={set("position")} />
                      </Field>
                      <Field label="Industry">
                        <select data-testid="enq-industry" className={selectCls} value={form.industry} onChange={set("industry")}>
                          <option value="">Select industry</option>
                          {INDUSTRIES.map((i) => (
                            <option key={i.label} value={i.label}>{i.label}</option>
                          ))}
                        </select>
                      </Field>
                    </div>
                  </div>

                  <div>
                    <p className="eyebrow mb-8">2 — About your space</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                      <Field label="Type of Space">
                        <select data-testid="enq-space-type" className={selectCls} value={form.space_type} onChange={set("space_type")}>
                          <option value="">Select space type</option>
                          {SPACE_TYPES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Approx. Size (m²)">
                        <input data-testid="enq-space-size" className={inputCls} placeholder="e.g. 200 m²" value={form.space_size} onChange={set("space_size")} />
                      </Field>
                      <Field label="Number of Locations">
                        <input data-testid="enq-locations" className={inputCls} placeholder="e.g. 3 locations" value={form.spaces} onChange={set("spaces")} />
                      </Field>
                      <Field label="Preferred Timeline">
                        <select data-testid="enq-timeline" className={selectCls} value={form.timeline} onChange={set("timeline")}>
                          <option value="">Select timeline</option>
                          {TIMELINES.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </Field>
                    </div>
                  </div>

                  <div>
                    <p className="eyebrow mb-8">3 — A few more details</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                      <Field label="Estimated Budget">
                        <select data-testid="enq-budget" className={selectCls} value={form.budget} onChange={set("budget")}>
                          <option value="">Select budget</option>
                          {BUDGETS.map((b) => (
                            <option key={b} value={b}>{b}</option>
                          ))}
                        </select>
                      </Field>
                      <Field label="How did you hear about us?">
                        <select data-testid="enq-hear" className={selectCls} value={form.hear_about} onChange={set("hear_about")}>
                          <option value="">Select an option</option>
                          {HEAR_ABOUT.map((h) => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </select>
                      </Field>
                    </div>
                    <div className="mt-8">
                      <Field label="Tell us about your goals *">
                        <textarea data-testid="enq-message" rows={4} className={`${inputCls} resize-none`} placeholder="What atmosphere are you hoping to create?" value={form.message} onChange={set("message")} />
                      </Field>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="enquiry-submit-button"
                    className="group inline-flex items-center gap-3 px-10 py-5 bg-gold text-[#1a120b] text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#ddc79f] transition-colors duration-300 disabled:opacity-60"
                  >
                    {loading ? "Sending…" : "Submit Enquiry"}
                    <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                </form>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-4 lg:border-l lg:border-cream lg:pl-14">
              <FadeUp>
                <p className="eyebrow mb-8">What happens next</p>
                <ol className="space-y-8">
                  {[
                    { t: "We review your space", d: "Our team maps your square metreage and airflow to size the right system." },
                    { t: "We design a scent strategy", d: "You receive a tailored recommendation and a no-obligation proposal." },
                    { t: "We install & support", d: "Self-install in minutes, or we install it for you — with ongoing servicing." },
                  ].map((step, i) => (
                    <li key={step.t} className="flex gap-4">
                      <span className="font-serif text-[#b89347] text-lg shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      <div>
                        <p className="text-ink font-serif text-lg leading-snug">{step.t}</p>
                        <p className="text-sm text-ink-muted mt-1 leading-relaxed">{step.d}</p>
                      </div>
                    </li>
                  ))}
                </ol>

                <div className="mt-12 pt-12 border-t border-cream">
                  <p className="eyebrow mb-4 text-[0.6rem]">Prefer to talk?</p>
                  <a href="tel:1300123456" className="block text-ink hover:text-gold transition-colors mb-2">1300 123 456</a>
                  <a href="mailto:hello@thescentbutler.com.au" className="text-sm text-gold link-underline">
                    hello@thescentbutler.com.au
                  </a>
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      <HowItWorks />
    </div>
  );
}
